import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { EventOut, EventCreate, EventVenueOut, EventVenueCreate, EventVenueUpdate, VenueTypeOut, EventConfig, UserOut, RecurrenceFrequency, RecurrenceTimeWindows, EventCloneRequest, LocalOut } from '@/types/api';
import { canAccessAllEvents, isSuperAdmin } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Tab from '@/components/atoms/Tab';
import Tabs from '@/components/atoms/Tabs';
import { FormField } from '@/components/molecules/FormField';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import { EUROPEAN_COUNTRY_CODES, countryName } from '@/utils/countries';
import { TranslationFields, TranslationFieldsHandle } from '@/components/molecules/TranslationFields';
import FileUpload from '@/components/atoms/FileUpload/FileUpload';
import Toggle from '@/components/atoms/Toggle/Toggle';
import './EventManagement.scss';

interface EventFormData {
  local_id: number | null;
  name: string;
  description: string;
  logo: string | null;
  start_date: string;
  end_date: string;
  duration_hours: number | '';
  is_recurring: boolean;
  recurrence_frequency: RecurrenceFrequency | '';
  recurrence_interval: number | '';
  recurrence_days: number[];
  recurrence_exceptions: string[];
  recurrence_time_windows: Record<string, { start_time: string; end_time: string; duration_hours: number | '' }>;
  address: string;
  city: string;
  state: string;
  country: string;
  active: boolean;
}

interface VenueFormData {
  name: string;
  description: string;
  type_id: number | null;
  zone: string;
  address: string;
  capacity: number | null;
  venue_image: string | null;
  is_active: boolean;
}

const initialVenueFormData: VenueFormData = {
  name: '',
  description: '',
  type_id: null,
  zone: '',
  address: '',
  capacity: null,
  venue_image: null,
  is_active: true
};

const initialFormData: EventFormData = {
  local_id: null,
  name: '',
  description: '',
  logo: null,
  start_date: '',
  end_date: '',
  duration_hours: '',
  is_recurring: true,
  recurrence_frequency: 'weekly',
  recurrence_interval: 1,
  recurrence_days: [4, 5],
  recurrence_exceptions: [],
  recurrence_time_windows: {},
  address: '',
  city: '',
  state: '',
  country: '',
  active: true
};

const EventManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState<EventOut[]>([]);
  const [deletedEvents, setDeletedEvents] = useState<EventOut[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'deleted'>('active');
  const [loading, setLoading] = useState(true);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventOut | null>(null);
  const eventTranslationsRef = useRef<TranslationFieldsHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [locals, setLocals] = useState<LocalOut[]>([]);
  
  // Venue management state
  const [venues, setVenues] = useState<Record<number, EventVenueOut[]>>({});
  const [venueTypes, setVenueTypes] = useState<VenueTypeOut[]>([]);
  const [showVenueForm, setShowVenueForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState<EventVenueOut | null>(null);
  const venueTranslationsRef = useRef<TranslationFieldsHandle>(null);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [venueFormData, setVenueFormData] = useState<VenueFormData>(initialVenueFormData);
  const [venueFormErrors, setVenueFormErrors] = useState<Record<string, string>>({});
  const [venueImageFile, setVenueImageFile] = useState<File | null>(null);
  const [deletedVenues, setDeletedVenues] = useState<Record<number, EventVenueOut[]>>({});
  const [showDeletedVenues, setShowDeletedVenues] = useState<Record<number, boolean>>({});
  const [loadingDeletedVenues, setLoadingDeletedVenues] = useState<Record<number, boolean>>({});
  const [duplicatingVenueId, setDuplicatingVenueId] = useState<number | null>(null);
  const [duplicateTargetEventId, setDuplicateTargetEventId] = useState<number | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [cloningEventId, setCloningEventId] = useState<number | null>(null);
  const [cloneFormData, setCloneFormData] = useState<EventCloneRequest>({ name: '', start_date: '', end_date: '' });
  const [isCloning, setIsCloning] = useState(false);
  const [recurrenceExceptionInput, setRecurrenceExceptionInput] = useState('');
  
  // Event configuration state
  const [eventConfigs, setEventConfigs] = useState<Record<number, EventConfig[]>>({});
  const [loadingConfigs, setLoadingConfigs] = useState<Record<number, boolean>>({});
  const [editingConfigs, setEditingConfigs] = useState<Record<string, string>>({});
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isSyncingConfigs, setIsSyncingConfigs] = useState<Record<number, boolean>>({});
  const [isVerifyingConfigs, setIsVerifyingConfigs] = useState<Record<number, boolean>>({});
  
  // Organizer management state (super admin only)
  const [organizers, setOrganizers] = useState<UserOut[]>([]);
  const [loadingOrganizers, setLoadingOrganizers] = useState(false);
  const [isUpdatingOrganizer, setIsUpdatingOrganizer] = useState<Record<number, boolean>>({});

  const recurrenceFrequencyOptions: { value: RecurrenceFrequency; label: string }[] = [
    { value: 'daily', label: t('management.events.recurrence.frequencyDaily') },
    { value: 'weekly', label: t('management.events.recurrence.frequencyWeekly') },
    { value: 'monthly', label: t('management.events.recurrence.frequencyMonthly') },
    { value: 'yearly', label: t('management.events.recurrence.frequencyYearly') }
  ];

  const recurrenceDayOptions = [
    { value: 0, label: t('management.events.recurrence.dayMon') },
    { value: 1, label: t('management.events.recurrence.dayTue') },
    { value: 2, label: t('management.events.recurrence.dayWed') },
    { value: 3, label: t('management.events.recurrence.dayThu') },
    { value: 4, label: t('management.events.recurrence.dayFri') },
    { value: 5, label: t('management.events.recurrence.daySat') },
    { value: 6, label: t('management.events.recurrence.daySun') }
  ];

  const getDefaultStartTime = useCallback((value?: string) => {
    if (!value || !value.includes('T')) return '22:00';
    const timePart = value.split('T')[1] || '';
    return timePart.slice(0, 5) || '22:00';
  }, []);

  const normalizeRecurrenceTimeWindows = useCallback((
    windows: EventFormData['recurrence_time_windows']
  ): RecurrenceTimeWindows | null => {
    const normalized: RecurrenceTimeWindows = {};
    Object.entries(windows).forEach(([day, window]) => {
      const start = window.start_time?.trim();
      const end = window.end_time?.trim();
      const duration = window.duration_hours === '' ? null : Number(window.duration_hours);
      if (!start) return;
      normalized[day] = {
        start_time: start,
        end_time: end || null,
        duration_hours: duration && !Number.isNaN(duration) ? duration : null
      };
    });
    return Object.keys(normalized).length ? normalized : null;
  }, []);

  // Fetch events for the current organizer or all events for super admin
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const eventsData = canAccessAllEvents(user)
        ? await apiService.getEvents()
        : await apiService.getMyManagedEvents();
      setEvents(eventsData);
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [t, user]);

  const fetchVenueTypes = useCallback(async () => {
    try {
      const typesData = await apiService.getVenueTypes();
      setVenueTypes(typesData);
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    }
  }, [t]);

  const fetchDeletedEvents = useCallback(async () => {
    try {
      setLoadingDeleted(true);
      const deletedData = await apiService.getDeletedEvents();
      setDeletedEvents(deletedData);
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setLoadingDeleted(false);
    }
  }, [t]);

  // Venue management functions
  const fetchVenues = useCallback(async (eventId: number) => {
    try {
      const venuesData = await apiService.getEventVenues(eventId);
      setVenues(prev => ({ ...prev, [eventId]: venuesData }));
    } catch (err: any) {

      setError(err.message || t('errors.general'));
    }
  }, [t]);

  const fetchDeletedVenues = useCallback(async (eventId: number) => {
    try {
      setLoadingDeletedVenues(prev => ({ ...prev, [eventId]: true }));
      const deletedData = await apiService.getDeletedEventVenues(eventId);
      setDeletedVenues(prev => ({ ...prev, [eventId]: deletedData }));
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setLoadingDeletedVenues(prev => ({ ...prev, [eventId]: false }));
    }
  }, [t]);

  const fetchLocals = useCallback(async () => {
    try {
      const localsData = await apiService.getLocals();
      setLocals(localsData);
    } catch (err: any) {
      console.error('Failed to fetch locals:', err);
    }
  }, []);

  // Fetch organizers (super admin only)
  const fetchOrganizers = useCallback(async () => {
    if (!isSuperAdmin(user)) return;

    try {
      setLoadingOrganizers(true);
      const organizersData = await apiService.getUsers({ role: 'organizer' });
      setOrganizers(organizersData);
    } catch (err: any) {
      console.error('Failed to fetch organizers:', err);
    } finally {
      setLoadingOrganizers(false);
    }
  }, [user]);

  useEffect(() => {
    fetchEvents();
    fetchVenueTypes();
    fetchLocals();
    if (isSuperAdmin(user)) {
      fetchOrganizers();
    }
    if (activeTab === 'deleted') {
      fetchDeletedEvents();
    }
  }, [fetchEvents, fetchVenueTypes, fetchLocals, activeTab, fetchDeletedEvents, fetchOrganizers, user]);

  // Load venues for all events when events are loaded
  useEffect(() => {
    if (events.length > 0) {
      events.forEach(event => {
        if (!venues[event.id]) {
          fetchVenues(event.id);
        }
      });
    }
  }, [events, venues, fetchVenues]);

  const handleInputChange = (field: keyof EventFormData, value: string | boolean | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRecurringToggle = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      is_recurring: checked,
      ...(checked ? {} : {
        recurrence_frequency: '',
        recurrence_interval: 1,
        recurrence_days: [],
        recurrence_exceptions: [],
        recurrence_time_windows: {}
      })
    }));
    if (!checked) {
      setRecurrenceExceptionInput('');
    }
    setFormErrors(prev => ({
      ...prev,
      recurrence_frequency: '',
      recurrence_interval: '',
      recurrence_days: '',
      recurrence_exceptions: '',
      recurrence_time_windows: '',
      end_date: ''
    }));
  };

  const handleRecurrenceDayToggle = (day: number) => {
    setFormData(prev => {
      const exists = prev.recurrence_days.includes(day);
      const nextDays = exists
        ? prev.recurrence_days.filter(value => value !== day)
        : [...prev.recurrence_days, day].sort((a, b) => a - b);
      const nextWindows = { ...prev.recurrence_time_windows };
      const dayKey = String(day);
      if (exists) {
        delete nextWindows[dayKey];
      } else if (!nextWindows[dayKey]) {
        nextWindows[dayKey] = {
          start_time: getDefaultStartTime(prev.start_date),
          end_time: '',
          duration_hours: ''
        };
      }
      return {
        ...prev,
        recurrence_days: nextDays,
        recurrence_time_windows: nextWindows
      };
    });
    if (formErrors.recurrence_days) {
      setFormErrors(prev => ({ ...prev, recurrence_days: '' }));
    }
  };

  const handleRecurrenceWindowChange = (
    day: number,
    field: 'start_time' | 'end_time' | 'duration_hours',
    value: string | number
  ) => {
    const dayKey = String(day);
    setFormData(prev => ({
      ...prev,
      recurrence_time_windows: {
        ...prev.recurrence_time_windows,
        [dayKey]: {
          start_time: prev.recurrence_time_windows[dayKey]?.start_time || '',
          end_time: prev.recurrence_time_windows[dayKey]?.end_time || '',
          duration_hours: prev.recurrence_time_windows[dayKey]?.duration_hours ?? '',
          [field]: value
        }
      }
    }));
    if (formErrors.recurrence_time_windows) {
      setFormErrors(prev => ({ ...prev, recurrence_time_windows: '' }));
    }
  };

  const handleAddRecurrenceException = () => {
    if (!recurrenceExceptionInput) return;
    setFormData(prev => {
      if (prev.recurrence_exceptions.includes(recurrenceExceptionInput)) {
        return prev;
      }
      return {
        ...prev,
        recurrence_exceptions: [...prev.recurrence_exceptions, recurrenceExceptionInput]
          .sort()
      };
    });
    setRecurrenceExceptionInput('');
    if (formErrors.recurrence_exceptions) {
      setFormErrors(prev => ({ ...prev, recurrence_exceptions: '' }));
    }
  };

  const handleRemoveRecurrenceException = (value: string) => {
    setFormData(prev => ({
      ...prev,
      recurrence_exceptions: prev.recurrence_exceptions.filter(item => item !== value)
    }));
  };

  const handleLogoChange = (file: File | null, previewUrl: string | null) => {
    setLogoFile(file);
    setFormData(prev => ({ ...prev, logo: previewUrl }));
    // Clear logo error when user selects a file
    if (formErrors.logo) {
      setFormErrors(prev => ({ ...prev, logo: '' }));
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = t('errors.validation.required');
    }

    if (!formData.start_date) {
      errors.start_date = t('errors.validation.required');
    }

    if (!formData.is_recurring && !formData.end_date) {
      errors.end_date = t('errors.validation.required');
    }

    if (!formData.end_date && !formData.duration_hours) {
      errors.duration_hours = t('errors.validation.required');
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate <= startDate) {
        errors.end_date = t('errors.validation.endDateAfterStart');
      }
    }

    if (formData.is_recurring) {
      if (!formData.recurrence_frequency) {
        errors.recurrence_frequency = t('errors.validation.required');
      }

      if (!formData.recurrence_interval || Number(formData.recurrence_interval) < 1) {
        errors.recurrence_interval = t('errors.validation.required');
      }

      if (formData.recurrence_frequency === 'weekly' && formData.recurrence_days.length === 0) {
        errors.recurrence_days = t('errors.validation.required');
      }

      if (formData.recurrence_frequency === 'weekly' && formData.recurrence_days.length > 0) {
        const fallbackDuration = formData.duration_hours ? Number(formData.duration_hours) : null;
        const hasInvalidWindow = formData.recurrence_days.some((day) => {
          const window = formData.recurrence_time_windows[String(day)];
          if (!window) return false;
          const start = window.start_time?.trim();
          const end = window.end_time?.trim();
          const duration = window.duration_hours;
          if (!start) return true;
          if (!end && (!duration || Number.isNaN(Number(duration))) && !fallbackDuration) return true;
          return false;
        });
        if (hasInvalidWindow) {
          errors.recurrence_time_windows = t('errors.validation.required');
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      
      const eventData: EventCreate = {
        local_id: formData.local_id,
        name: formData.name,
        description: formData.description || null,
        logo: null, // Will be set after upload if file exists
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        duration_hours: formData.duration_hours ? Number(formData.duration_hours) : null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        country: formData.country || null,
        active: formData.active
      };

      if (formData.is_recurring) {
        eventData.is_recurring = true;
        eventData.recurrence_frequency = formData.recurrence_frequency || null;
        eventData.recurrence_interval = formData.recurrence_interval ? Number(formData.recurrence_interval) : null;
        eventData.recurrence_days = formData.recurrence_frequency === 'weekly'
          ? formData.recurrence_days
          : null;
        const normalizedWindows = normalizeRecurrenceTimeWindows(formData.recurrence_time_windows);
        if (normalizedWindows && Object.keys(normalizedWindows).length > 0) {
          eventData.recurrence_frequency = 'weekly';
          eventData.recurrence_time_windows = normalizedWindows;
        } else {
          eventData.recurrence_time_windows = formData.recurrence_frequency === 'weekly'
            ? null
            : null;
        }
        eventData.recurrence_exceptions = formData.recurrence_exceptions.length
          ? formData.recurrence_exceptions
          : null;
      } else {
        eventData.is_recurring = false;
      }

      let createdEvent: EventOut;

      if (editingEvent) {
        // Upload logo first if new file was selected
        if (logoFile) {
          const logoResponse = await apiService.uploadImage(logoFile);
          eventData.logo = logoResponse.url;
        } else if (formData.logo && !logoFile) {
          // Keep existing logo if no new file was selected
          eventData.logo = formData.logo;
        }
        
        // Update existing event
        createdEvent = await apiService.updateEvent(editingEvent.id, eventData);
        await eventTranslationsRef.current?.save();
      } else {
        // Upload logo first if file was selected
        if (logoFile) {
          const logoResponse = await apiService.uploadImage(logoFile);
          eventData.logo = logoResponse.url;
        }
        
        // Create new event
        createdEvent = await apiService.createEvent(eventData);
      }

      // Reset form
      setFormData(initialFormData);
      setShowCreateForm(false);
      setEditingEvent(null);
      setLogoFile(null);
      
      // Refresh events
      await fetchEvents();
      
      // Fetch configs for the created/updated event (super admin only)
      if (createdEvent && isSuperAdmin(user)) {
        await fetchEventConfigs(createdEvent.id);
      }
      
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (event: EventOut) => {
    setEditingEvent(event);
    setFormData({
      local_id: event.local_id ?? null,
      name: event.name,
      description: event.description || '',
      logo: event.logo || null,
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      duration_hours: event.duration_hours ?? '',
      is_recurring: !!event.is_recurring,
      recurrence_frequency: event.recurrence_frequency || '',
      recurrence_interval: event.recurrence_interval ?? 1,
      recurrence_days: event.recurrence_days || [],
      recurrence_exceptions: event.recurrence_exceptions || [],
      recurrence_time_windows: event.recurrence_time_windows
        ? Object.fromEntries(
            Object.entries(event.recurrence_time_windows).map(([day, window]) => ([
              day,
              {
                start_time: window.start_time || '',
                end_time: window.end_time ?? '',
                duration_hours: window.duration_hours ?? ''
              }
            ]))
          )
        : {},
      address: event.address || '',
      city: event.city || '',
      state: event.state || '',
      country: event.country || '',
      active: event.active
    });
    setRecurrenceExceptionInput('');
    setLogoFile(null); // Reset file when editing
    setShowCreateForm(true);
    // Fetch configs for the event being edited (super admin only)
    if (isSuperAdmin(user)) {
      fetchEventConfigs(event.id);
    }
    // Refresh organizers list if super admin
    if (isSuperAdmin(user)) {
      fetchOrganizers();
    }
  };

  const handleDelete = async (eventId: number) => {
    if (window.confirm(t('management.events.confirmDelete'))) {
      try {
        await apiService.deleteEvent(eventId);
        await fetchEvents();
      } catch (err: any) {
        setError(err.message || t('errors.general'));
      }
    }
  };

  const handleToggleActive = async (eventId: number) => {
    try {
      await apiService.toggleEventActive(eventId);
      await fetchEvents();
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    }
  };

  const handleRestoreEvent = async (eventId: number) => {
    if (window.confirm(t('management.events.confirmRestore'))) {
      try {
        await apiService.restoreEvent(eventId);
        await fetchDeletedEvents();
        await fetchEvents();
        // Switch to active tab after restore
        setActiveTab('active');
      } catch (err: any) {
        setError(err.message || t('errors.general'));
      }
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setShowCreateForm(false);
    setEditingEvent(null);
    setFormErrors({});
    setLogoFile(null);
    setRecurrenceExceptionInput('');
  };

  const handleVenueInputChange = (field: keyof VenueFormData, value: string | number | boolean | null) => {
    setVenueFormData(prev => ({ ...prev, [field]: value }));
    if (venueFormErrors[field]) {
      setVenueFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleVenueImageChange = (file: File | null, previewUrl: string | null) => {
    setVenueImageFile(file);
    setVenueFormData(prev => ({ ...prev, venue_image: previewUrl }));
    if (venueFormErrors.venue_image) {
      setVenueFormErrors(prev => ({ ...prev, venue_image: '' }));
    }
  };

  const validateVenueForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!venueFormData.name.trim()) {
      errors.name = t('errors.validation.required');
    }

    if (!venueFormData.type_id) {
      errors.type_id = t('errors.validation.required');
    }

    setVenueFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleVenueSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateVenueForm() || !selectedEventId) return;

    try {
      setIsSubmitting(true);
      
      let createdVenue: EventVenueOut;

      if (editingVenue) {
        // Update existing venue
        const updateData: EventVenueUpdate = {
          name: venueFormData.name,
          description: venueFormData.description || null,
          type_id: venueFormData.type_id,
          zone: venueFormData.zone || null,
          address: venueFormData.address || null,
          capacity: venueFormData.capacity,
          is_active: venueFormData.is_active
        };

        // Handle image upload/update
        if (venueImageFile) {
          // New image file selected - upload it
          const imageResponse = await apiService.uploadImage(venueImageFile);
          updateData.venue_image = imageResponse.url;
        } else if (venueFormData.venue_image && !venueImageFile) {
          // Keep existing image
          updateData.venue_image = venueFormData.venue_image;
        }
        // If no image handling needed, don't include venue_image in updateData
        
        createdVenue = await apiService.updateEventVenue(selectedEventId, editingVenue.id, updateData);
        await venueTranslationsRef.current?.save();
      } else {
        // Create new venue
        const createData: EventVenueCreate = {
          name: venueFormData.name,
          description: venueFormData.description || null,
          type_id: venueFormData.type_id,
          zone: venueFormData.zone || null,
          address: venueFormData.address || null,
          capacity: venueFormData.capacity,
          venue_image: null,
          is_active: venueFormData.is_active,
          event_id: selectedEventId
        };

        // Upload image first if file was selected
        if (venueImageFile) {
          const imageResponse = await apiService.uploadImage(venueImageFile);
          createData.venue_image = imageResponse.url;
        }
        
        createdVenue = await apiService.createEventVenue(selectedEventId, createData);
      }

      // Reset form
      setVenueFormData(initialVenueFormData);
      setShowVenueForm(false);
      setEditingVenue(null);
      setVenueImageFile(null);
      
      // Refresh venues
      await fetchVenues(selectedEventId);
      
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVenueEdit = (venue: EventVenueOut) => {
    setEditingVenue(venue);
    setSelectedEventId(venue.event_id); // Set the selectedEventId to the venue's event
    setVenueFormData({
      name: venue.name,
      description: venue.description || '',
      type_id: venue.type_id || null,
      zone: venue.zone || '',
      address: venue.address || '',
      capacity: venue.capacity || null,
      venue_image: venue.venue_image || null,
      is_active: venue.is_active
    });
    setVenueImageFile(null);
    setShowVenueForm(true);
  };

  const handleVenueDelete = async (eventId: number, venueId: number) => {
    if (window.confirm(t('management.events.venues.confirmDelete'))) {
      try {
        await apiService.deleteEventVenue(eventId, venueId);
        await fetchVenues(eventId);
      } catch (err: any) {
        setError(err.message || t('errors.general'));
      }
    }
  };

  const handleToggleDeletedVenues = (eventId: number) => {
    const willShow = !showDeletedVenues[eventId];
    setShowDeletedVenues(prev => ({ ...prev, [eventId]: willShow }));
    if (willShow && !deletedVenues[eventId]) {
      fetchDeletedVenues(eventId);
    }
  };

  const handleRestoreVenue = async (eventId: number, venueId: number) => {
    if (window.confirm(t('management.events.venues.confirmRestore'))) {
      try {
        await apiService.restoreEventVenue(eventId, venueId);
        await fetchDeletedVenues(eventId);
        await fetchVenues(eventId);
      } catch (err: any) {
        setError(err.message || t('errors.general'));
      }
    }
  };

  const handleVenueCancel = () => {
    setVenueFormData(initialVenueFormData);
    setShowVenueForm(false);
    setEditingVenue(null);
    setVenueFormErrors({});
    setVenueImageFile(null);
  };

  const openVenueForm = (eventId: number) => {
    setSelectedEventId(eventId);
    setShowVenueForm(true);
    // Fetch venues for this event if not already loaded
    if (!venues[eventId]) {
      fetchVenues(eventId);
    }
  };

  const handleStartDuplicateVenue = (venueId: number) => {
    setDuplicatingVenueId(venueId);
    setDuplicateTargetEventId(null);
  };

  const handleCancelDuplicateVenue = () => {
    setDuplicatingVenueId(null);
    setDuplicateTargetEventId(null);
  };

  const handleConfirmDuplicateVenue = async (venueId: number) => {
    if (!duplicateTargetEventId) return;
    try {
      setIsDuplicating(true);
      const result = await apiService.duplicateEventVenue(venueId, duplicateTargetEventId);
      await fetchVenues(duplicateTargetEventId);
      window.alert(
        t('management.events.venues.duplicateSuccess', {
          drinks: result.drinks_duplicated,
          foods: result.foods_duplicated
        })
      );
      setDuplicatingVenueId(null);
      setDuplicateTargetEventId(null);
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleStartCloneEvent = (event: EventOut) => {
    setCloningEventId(event.id);
    setCloneFormData({
      name: t('management.events.cloneDefaultName', { name: event.name }),
      start_date: event.start_date || '',
      end_date: event.end_date || '',
      duration_hours: event.duration_hours ?? undefined
    });
  };

  const handleCancelCloneEvent = () => {
    setCloningEventId(null);
    setCloneFormData({ name: '', start_date: '', end_date: '' });
  };

  const handleConfirmCloneEvent = async (eventId: number) => {
    if (!cloneFormData.name || !cloneFormData.start_date) return;
    try {
      setIsCloning(true);
      const result = await apiService.cloneEvent(eventId, cloneFormData);
      await fetchEvents();
      window.alert(
        t('management.events.cloneSuccess', {
          venues: result.venues_cloned,
          drinks: result.drinks_cloned + result.foods_cloned,
          team: result.team_moved
        })
      );
      setCloningEventId(null);
      setCloneFormData({ name: '', start_date: '', end_date: '' });
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsCloning(false);
    }
  };

  // Event configuration functions
  const fetchEventConfigs = useCallback(async (eventId: number) => {
    try {
      setLoadingConfigs(prev => ({ ...prev, [eventId]: true }));
      const response = await apiService.getEventConfigs(eventId);
      setEventConfigs(prev => ({ ...prev, [eventId]: response.configs }));
    } catch (err: any) {
      console.error('Failed to fetch event configs:', err);
      setError(err.message || t('errors.general'));
    } finally {
      setLoadingConfigs(prev => ({ ...prev, [eventId]: false }));
    }
  }, [t]);

  const handleConfigValueChange = (configKey: string, value: string) => {
    setEditingConfigs(prev => ({ ...prev, [configKey]: value }));
  };

  const handleSaveConfig = async (eventId: number, configKey: string) => {
    const configValue = editingConfigs[configKey];
    if (configValue === undefined) return;

    try {
      setIsSavingConfig(true);
      await apiService.updateEventConfig(eventId, configKey, configValue);
      await fetchEventConfigs(eventId);
      setEditingConfigs(prev => {
        const newConfigs = { ...prev };
        delete newConfigs[configKey];
        return newConfigs;
      });
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleToggleBooleanConfig = async (eventId: number, configKey: string, newValue: boolean) => {
    try {
      setIsSavingConfig(true);
      await apiService.updateEventConfig(eventId, configKey, newValue.toString());
      await fetchEventConfigs(eventId);
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleSyncConfigs = async (eventId: number) => {
    try {
      setIsSyncingConfigs(prev => ({ ...prev, [eventId]: true }));
      await apiService.syncEventConfigs(eventId);
      await fetchEventConfigs(eventId);
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsSyncingConfigs(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const handleVerifyConfigs = async (eventId: number) => {
    try {
      setIsVerifyingConfigs(prev => ({ ...prev, [eventId]: true }));
      const response = await apiService.verifyEventConfigs(eventId);
      if (response.errors && response.errors.length > 0) {
        setError(response.errors.join(', '));
      }
      await fetchEventConfigs(eventId);
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsVerifyingConfigs(prev => ({ ...prev, [eventId]: false }));
    }
  };

  // Helper function to get organizer from organizers list
  const getOrganizerById = (organizerId: number | null | undefined): UserOut | null => {
    if (!organizerId) return null;
    return organizers.find(org => org.id === organizerId) || null;
  };

  // Helper function to get organizer display name
  const getOrganizerDisplayName = (organizer: UserOut | null): string => {
    if (!organizer) return '';
    if (organizer.first_name && organizer.last_name) {
      return `${organizer.first_name} ${organizer.last_name}`;
    }
    return organizer.username || organizer.email || '';
  };

  // Organizer management functions (super admin only)
  const handleChangeOrganizer = async (eventId: number, organizerId: number) => {
    try {
      setIsUpdatingOrganizer(prev => ({ ...prev, [eventId]: true }));
      const updatedEvent = await apiService.updateEventOrganizer(eventId, organizerId);
      await fetchEvents();
      // Update the editing event if it's the same event being edited
      if (editingEvent && editingEvent.id === eventId) {
        setEditingEvent(updatedEvent);
      }
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setIsUpdatingOrganizer(prev => ({ ...prev, [eventId]: false }));
    }
  };
  
  if (!canAccessAllEvents(user)) {
    return (
      <div className="event-management-page">
        <div className="container">
          <Card variant="elevated" padding="lg">
            <h1>{t('errors.unauthorized')}</h1>
            <p>{t('management.events.organizerOnly')}</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="event-management-page">
      <div className="container">
        <div className="event-management-page__header">
          <h1 className="event-management-page__title">
            {t('management.events.title')}
          </h1>
          <p className="event-management-page__description">
            {t('management.events.description')}
          </p>
          
          <Tabs className="event-management-page__tabs">
            <Tab
              active={activeTab === 'active'}
              onClick={() => setActiveTab('active')}
            >
              {t('management.events.activeEvents')}
            </Tab>
            <Tab
              active={activeTab === 'deleted'}
              onClick={() => {
                setActiveTab('deleted');
                if (deletedEvents.length === 0) {
                  fetchDeletedEvents();
                }
              }}
            >
              {t('management.events.deletedEvents')}
            </Tab>
          </Tabs>
          
          {activeTab === 'active' && (
            <Button
              variant="primary"
              onClick={() => setShowCreateForm(true)}
              className="event-management-page__create-btn"
            >
              {t('management.events.createEvent')}
            </Button>
          )}
        </div>

        {error && (
          <Card variant="elevated" padding="md" className="event-management-page__error">
            <p>{error}</p>
          </Card>
        )}

        {/* Create/Edit Event Form */}
        {showCreateForm && (
          <Card variant="elevated" padding="lg" className="event-management-page__form">
            <h2>{editingEvent ? t('management.events.editEvent') : t('management.events.createEvent')}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="event-management-page__form-grid">

                <FileUpload
                  name="logo"
                  label={t('management.events.logo')}
                  value={formData.logo}
                  onChange={handleLogoChange}
                  error={formErrors.logo}
                  accept="image/*"
                  maxSize={5}
                  disabled={isSubmitting}
                  placeholder={t('management.events.logoPlaceholder')}
                />

                <FormField
                  type="text"
                  name="name"
                  label={t('management.events.name')}
                  error={formErrors.name}
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('management.events.namePlaceholder')}
                  disabled={isSubmitting}
                />

                <SearchableSelect<number>
                  name="local_id"
                  label={t('management.events.local')}
                  helperText={t('management.events.localHelper')}
                  placeholder={t('locals.noLocal')}
                  clearable
                  options={locals.map((local) => ({ value: local.id, label: local.name }))}
                  value={formData.local_id ?? null}
                  onChange={(value) => handleInputChange('local_id', value)}
                  disabled={isSubmitting}
                />

                <FormField
                  type="textarea"
                  name="description"
                  label={t('management.events.description')}
                  error={formErrors.description}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={t('management.events.descriptionPlaceholder')}
                  disabled={isSubmitting}
                />

                

                <FormField
                  type="datetime-local"
                  name="start_date"
                  label={t('management.events.startDate')}
                  error={formErrors.start_date}
                  required
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  disabled={isSubmitting}
                />

                <FormField
                  type="datetime-local"
                  name="end_date"
                  label={t('management.events.endDate')}
                  error={formErrors.end_date}
                  required={!formData.is_recurring}
                  helperText={formData.is_recurring ? t('management.events.recurrence.endDateHelper') : undefined}
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  disabled={isSubmitting}
                />

                <FormField
                  type="number"
                  name="duration_hours"
                  label={t('management.events.durationHours')}
                  error={formErrors.duration_hours}
                  value={formData.duration_hours}
                  min="1"
                  step="1"
                  onChange={(e) => {
                    const nextValue = e.target.value === '' ? '' : Number(e.target.value);
                    handleInputChange('duration_hours', nextValue);
                  }}
                  helperText={t('management.events.durationHoursHelper')}
                  disabled={isSubmitting}
                />

                <div className="event-management-page__recurrence-toggle">
                  <Toggle
                    checked={formData.is_recurring}
                    onChange={handleRecurringToggle}
                    disabled={isSubmitting}
                    label={t('management.events.recurrence.isRecurring')}
                  />
                </div>

                {formData.is_recurring && (
                  <>
                    <FormField
                      type="select"
                      name="recurrence_frequency"
                      label={t('management.events.recurrence.frequency')}
                      error={formErrors.recurrence_frequency}
                      required
                      value={formData.recurrence_frequency}
                      onChange={(e) => handleInputChange('recurrence_frequency', e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">{t('management.events.recurrence.selectFrequency')}</option>
                      {recurrenceFrequencyOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormField>

                    <FormField
                      type="number"
                      name="recurrence_interval"
                      label={t('management.events.recurrence.interval')}
                      error={formErrors.recurrence_interval}
                      required
                      value={formData.recurrence_interval}
                      min="1"
                      step="1"
                      onChange={(e) => {
                        const nextValue = e.target.value === '' ? '' : Number(e.target.value);
                        handleInputChange('recurrence_interval', nextValue);
                      }}
                      disabled={isSubmitting}
                      helperText={t('management.events.recurrence.intervalHelper')}
                    />

                    {formData.recurrence_frequency === 'weekly' && (
                      <div className="event-management-page__recurrence-days">
                        <label className="event-management-page__recurrence-days-label">
                          {t('management.events.recurrence.days')}
                          <span className="form-field__required">*</span>
                        </label>
                        <div className="event-management-page__recurrence-days-options">
                          {recurrenceDayOptions.map(option => {
                            const checked = formData.recurrence_days.includes(option.value);
                            return (
                              <label key={option.value} className="event-management-page__recurrence-day">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleRecurrenceDayToggle(option.value)}
                                  disabled={isSubmitting}
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                        {formErrors.recurrence_days && (
                          <div className="form-field__error" role="alert">
                            {formErrors.recurrence_days}
                          </div>
                        )}
                      </div>
                    )}

                    {formData.recurrence_frequency === 'weekly' && (
                      <div className="event-management-page__recurrence-windows">
                        <label className="event-management-page__recurrence-windows-label">
                          {t('management.events.recurrence.timeWindows')}
                        </label>
                        <div className="event-management-page__recurrence-windows-list">
                          {formData.recurrence_days.map((day) => {
                            const option = recurrenceDayOptions.find(item => item.value === day);
                            const window = formData.recurrence_time_windows[String(day)] || {
                              start_time: '',
                              end_time: '',
                              duration_hours: ''
                            };
                            return (
                              <div key={day} className="event-management-page__recurrence-window">
                                <span className="event-management-page__recurrence-window-day">
                                  {option?.label || day}
                                </span>
                                <div className="event-management-page__recurrence-window-fields">
                                  <label className="event-management-page__recurrence-window-field">
                                    <span>{t('management.events.recurrence.startTime')}</span>
                                    <input
                                      type="time"
                                      value={window.start_time}
                                      onChange={(e) => handleRecurrenceWindowChange(day, 'start_time', e.target.value)}
                                      disabled={isSubmitting}
                                    />
                                  </label>
                                  <label className="event-management-page__recurrence-window-field">
                                    <span>{t('management.events.recurrence.endTime')}</span>
                                    <input
                                      type="time"
                                      value={window.end_time}
                                      onChange={(e) => handleRecurrenceWindowChange(day, 'end_time', e.target.value)}
                                      disabled={isSubmitting}
                                    />
                                  </label>
                                  <span className="event-management-page__recurrence-window-or">
                                    {t('management.events.recurrence.or')}
                                  </span>
                                  <label className="event-management-page__recurrence-window-field">
                                    <span>{t('management.events.recurrence.durationHours')}</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.5"
                                      value={window.duration_hours === '' ? '' : window.duration_hours}
                                      onChange={(e) => {
                                        const nextValue = e.target.value === '' ? '' : Number(e.target.value);
                                        handleRecurrenceWindowChange(day, 'duration_hours', nextValue);
                                      }}
                                      disabled={isSubmitting}
                                    />
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {formErrors.recurrence_time_windows && (
                          <div className="form-field__error" role="alert">
                            {formErrors.recurrence_time_windows}
                          </div>
                        )}
                        <p className="event-management-page__recurrence-windows-helper">
                          {t('management.events.recurrence.timeWindowsHelper')}
                        </p>
                      </div>
                    )}

                    <div className="event-management-page__recurrence-exceptions">
                      <label className="event-management-page__recurrence-days-label">
                        {t('management.events.recurrence.exceptions')}
                      </label>
                      <div className="event-management-page__recurrence-exceptions-input">
                        <FormField
                          type="date"
                          name="recurrence_exception_input"
                          value={recurrenceExceptionInput}
                          onChange={(e) => setRecurrenceExceptionInput(e.target.value)}
                          disabled={isSubmitting}
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          disabled={!recurrenceExceptionInput || isSubmitting}
                          onClick={handleAddRecurrenceException}
                        >
                          {t('management.events.recurrence.addException', {
                            defaultValue: t('events.recurrence.addException')
                          })}
                        </Button>
                      </div>
                      {formErrors.recurrence_exceptions && (
                        <div className="form-field__error" role="alert">
                          {formErrors.recurrence_exceptions}
                        </div>
                      )}
                      {formData.recurrence_exceptions.length > 0 ? (
                        <ul className="event-management-page__recurrence-exceptions-list">
                          {formData.recurrence_exceptions.map((exception) => (
                            <li key={exception} className="event-management-page__recurrence-exceptions-item">
                              <span>{exception}</span>
                              <Button
                                variant="ghost"
                                size="xs"
                                type="button"
                                onClick={() => handleRemoveRecurrenceException(exception)}
                              >
                                {t('management.events.recurrence.remove', {
                                  defaultValue: t('events.recurrence.remove')
                                })}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="event-management-page__recurrence-exceptions-empty">
                          {t('management.events.recurrence.noExceptions', {
                            defaultValue: t('events.recurrence.noExceptions')
                          })}
                        </p>
                      )}
                      <span className="event-management-page__recurrence-exceptions-helper">
                        {t('management.events.recurrence.exceptionsHelper')}
                      </span>
                    </div>
                  </>
                )}

                <FormField
                  type="text"
                  name="address"
                  label={t('management.events.address')}
                  error={formErrors.address}
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder={t('management.events.addressPlaceholder')}
                  disabled={isSubmitting}
                />

                <FormField
                  type="text"
                  name="city"
                  label={t('management.events.city')}
                  error={formErrors.city}
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder={t('management.events.cityPlaceholder')}
                  disabled={isSubmitting}
                />

                <FormField
                  type="text"
                  name="state"
                  label={t('management.events.state')}
                  error={formErrors.state}
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder={t('management.events.statePlaceholder')}
                  disabled={isSubmitting}
                />

                {/* Stores the ISO code (e.g. "ES"): the carta's default tax rate is looked up by it. */}
                <SearchableSelect<string>
                  name="country"
                  label={t('management.events.country')}
                  error={formErrors.country}
                  helperText={t('management.events.countryHelp')}
                  placeholder={t('management.events.countryPlaceholder')}
                  clearable
                  options={(() => {
                    const options = EUROPEAN_COUNTRY_CODES
                      .map((code) => ({ value: code as string, label: `${countryName(code, i18n.language)} (${code})` }))
                      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
                    // An older event may hold free text ("España") or a non-European code: keep it visible.
                    return formData.country && !options.some((o) => o.value === formData.country)
                      ? [{ value: formData.country, label: formData.country }, ...options]
                      : options;
                  })()}
                  value={formData.country || null}
                  onChange={(value) => handleInputChange('country', value ?? '')}
                  disabled={isSubmitting}
                />

                {/* Organizer selector - Super admin only */}
                {isSuperAdmin(user) && editingEvent && (
                  <div className="event-management-page__organizer-field">
                    <label className="event-management-page__organizer-label">
                      {t('management.events.organizer')}
                    </label>
                    <div className="event-management-page__organizer-selector">
                      <SearchableSelect<number>
                        name="organizer_id"
                        placeholder={t('management.events.noOrganizer')}
                        options={organizers.map((organizer) => ({
                          value: organizer.id,
                          label: `${getOrganizerDisplayName(organizer)} (${organizer.email})`,
                        }))}
                        value={editingEvent.organizer_id || null}
                        onChange={(newOrganizerId) => {
                          if (newOrganizerId && editingEvent.organizer_id !== newOrganizerId) {
                            handleChangeOrganizer(editingEvent.id, newOrganizerId);
                          }
                        }}
                        disabled={isUpdatingOrganizer[editingEvent.id] || isSubmitting}
                      />
                      {editingEvent.organizer_id && (() => {
                        const currentOrganizer = editingEvent.organizer || getOrganizerById(editingEvent.organizer_id);
                        return currentOrganizer ? (
                          <div className="event-management-page__current-organizer">
                            <span className="event-management-page__organizer-info">
                              {t('management.events.currentOrganizer')}:{' '}
                              {getOrganizerDisplayName(currentOrganizer)}
                              {currentOrganizer.email && ` (${currentOrganizer.email})`}
                            </span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                )}
                
              </div>

              {editingEvent && (
                <TranslationFields
                  ref={eventTranslationsRef}
                  entityType="event"
                  entityId={editingEvent.id}
                  disabled={isSubmitting}
                />
              )}

              <div className="event-management-page__form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('common.loading') : (editingEvent ? t('common.update') : t('common.create'))}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Event Configuration Section - Only show when editing an existing event and user is super admin */}
        {editingEvent && isSuperAdmin(user) && (
          <Card variant="elevated" padding="lg" className="event-management-page__config-section">
            <div className="event-management-page__config-header">
              <h2>{t('management.events.configs.title')}</h2>
              <div className="event-management-page__config-actions">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleSyncConfigs(editingEvent.id)}
                  disabled={isSyncingConfigs[editingEvent.id]}
                >
                  {isSyncingConfigs[editingEvent.id] ? t('common.loading') : t('management.events.configs.sync')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleVerifyConfigs(editingEvent.id)}
                  disabled={isVerifyingConfigs[editingEvent.id]}
                >
                  {isVerifyingConfigs[editingEvent.id] ? t('common.loading') : t('management.events.configs.verify')}
                </Button>
              </div>
            </div>
            
            {loadingConfigs[editingEvent.id] ? (
              <p>{t('common.loading')}</p>
            ) : eventConfigs[editingEvent.id] && eventConfigs[editingEvent.id].length > 0 ? (
              <div className="event-management-page__configs-list">
                {eventConfigs[editingEvent.id].map((config) => {
                  const isBoolean = config.config_type === 'boolean';
                  const isEditing = editingConfigs[config.config_key] !== undefined;
                  const displayValue = isEditing ? editingConfigs[config.config_key] : config.config_value;
                  const booleanValue = String(displayValue).toLowerCase() === 'true';
                  
                  return (
                    <div key={config.config_key} className="event-management-page__config-item">
                      <div className="event-management-page__config-info">
                        <label className="event-management-page__config-key">
                          {config.config_key}
                          {config.description && (
                            <span className="event-management-page__config-description">
                              {config.description}
                            </span>
                          )}
                        </label>
                        {isBoolean ? (
                          <Toggle
                            checked={booleanValue}
                            onChange={(checked) => {
                              handleToggleBooleanConfig(editingEvent.id, config.config_key, checked);
                            }}
                            disabled={isSavingConfig}
                          />
                        ) : isEditing ? (
                          <FormField
                            type={config.config_type === 'number' ? 'number' : 'text'}
                            name={`config_${config.config_key}`}
                            value={displayValue}
                            onChange={(e) => handleConfigValueChange(config.config_key, e.target.value)}
                            disabled={isSavingConfig}
                          />
                        ) : (
                          <div className="event-management-page__config-value">
                            {config.config_value || <em>{t('management.events.configs.noValue')}</em>}
                          </div>
                        )}
                        <div className="event-management-page__config-status">
                          {config.is_synced && (
                            <span className="event-management-page__status-badge synced">
                              {t('management.events.configs.synced')}
                            </span>
                          )}
                          {config.is_verified && (
                            <span className="event-management-page__status-badge verified">
                              {t('management.events.configs.verified')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="event-management-page__config-actions-item">
                        {!isBoolean && isEditing ? (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSaveConfig(editingEvent.id, config.config_key)}
                              disabled={isSavingConfig}
                            >
                              {t('common.save')}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingConfigs(prev => {
                                  const newConfigs = { ...prev };
                                  delete newConfigs[config.config_key];
                                  return newConfigs;
                                });
                              }}
                              disabled={isSavingConfig}
                            >
                              {t('common.cancel')}
                            </Button>
                          </>
                        ) : !isBoolean ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleConfigValueChange(config.config_key, config.config_value)}
                          >
                            {t('common.edit')}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="event-management-page__no-configs">
                {t('management.events.configs.noConfigs')}
              </p>
            )}
          </Card>
        )}

        {/* Create/Edit Venue Form */}
        {showVenueForm && (
          <Card variant="elevated" padding="lg" className="event-management-page__venue-form">
            <h2>{editingVenue ? t('management.events.venues.editVenue') : t('management.events.venues.addVenue')}</h2>
            
            <form onSubmit={handleVenueSubmit}>
              <div className="event-management-page__venue-form-grid">
                <FormField
                  type="text"
                  name="name"
                  label={t('management.events.venues.name')}
                  error={venueFormErrors.name}
                  required
                  value={venueFormData.name}
                  onChange={(e) => handleVenueInputChange('name', e.target.value)}
                  placeholder={t('management.events.venues.namePlaceholder')}
                  disabled={isSubmitting}
                />

                <FormField
                  type="textarea"
                  name="description"
                  label={t('management.events.venues.description')}
                  error={venueFormErrors.description}
                  value={venueFormData.description}
                  onChange={(e) => handleVenueInputChange('description', e.target.value)}
                  placeholder={t('management.events.venues.descriptionPlaceholder')}
                  disabled={isSubmitting}
                />

                <SearchableSelect<number>
                  name="type_id"
                  label={t('management.events.venues.type')}
                  placeholder={t('management.events.venues.selectType')}
                  error={venueFormErrors.type_id}
                  required
                  options={venueTypes.map((type) => ({ value: type.id, label: type.name }))}
                  value={venueFormData.type_id || null}
                  onChange={(value) => handleVenueInputChange('type_id', value)}
                  disabled={isSubmitting}
                />

                <FormField
                  type="text"
                  name="zone"
                  label={t('management.events.venues.zone')}
                  error={venueFormErrors.zone}
                  value={venueFormData.zone}
                  onChange={(e) => handleVenueInputChange('zone', e.target.value)}
                  placeholder={t('management.events.venues.zonePlaceholder')}
                  disabled={isSubmitting}
                />

                <FormField
                  type="text"
                  name="address"
                  label={t('management.events.venues.address')}
                  error={venueFormErrors.address}
                  value={venueFormData.address}
                  onChange={(e) => handleVenueInputChange('address', e.target.value)}
                  placeholder={t('management.events.venues.addressPlaceholder')}
                  disabled={isSubmitting}
                />

                <FormField
                  type="number"
                  name="capacity"
                  label={t('management.events.venues.capacity')}
                  error={venueFormErrors.capacity}
                  value={venueFormData.capacity?.toString() || ''}
                  onChange={(e) => handleVenueInputChange('capacity', parseInt(e.target.value) || null)}
                  placeholder={t('management.events.venues.capacityPlaceholder')}
                  disabled={isSubmitting}
                />

                <FileUpload
                  name="venue_image"
                  label={t('management.events.venues.image')}
                  value={venueFormData.venue_image}
                  onChange={handleVenueImageChange}
                  error={venueFormErrors.venue_image}
                  accept="image/*"
                  maxSize={5}
                  disabled={isSubmitting}
                  placeholder={t('management.events.venues.imagePlaceholder')}
                />
              </div>

              {editingVenue && (
                <TranslationFields
                  ref={venueTranslationsRef}
                  entityType="event_venue"
                  entityId={editingVenue.id}
                  disabled={isSubmitting}
                />
              )}

              <div className="event-management-page__venue-form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('common.loading') : (editingVenue ? t('common.update') : t('common.create'))}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleVenueCancel}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Events List */}
        <div className="event-management-page__events">
          {activeTab === 'active' ? (
            loading ? (
              <Card variant="elevated" padding="lg">
                <p>{t('common.loading')}</p>
              </Card>
            ) : events.length === 0 ? (
              <Card variant="elevated" padding="lg">
                <p>{t('management.events.noEvents')}</p>
              </Card>
            ) : (
              <div className="event-management-page__events-grid">
                {events.map((event) => (
                  <Card key={event.id} variant="elevated" padding="lg" className="event-management-page__event-card">
                    <div className="event-management-page__event-header">
                      <div className="event-management-page__event-title-section">
                        {event.logo && (
                          <img 
                            src={event.logo} 
                            alt={`${event.name} logo`}
                            className="event-management-page__event-logo"
                          />
                        )}
                        <h3>{event.name}</h3>
                      </div>
                      <div className="event-management-page__event-status">
                        <span className={`event-management-page__status-badge ${event.active ? 'active' : 'inactive'}`}>
                          {event.active ? t('management.events.active') : t('management.events.inactive')}
                        </span>
                      </div>
                    </div>
                    
                    {event.description && (
                      <p className="event-management-page__event-description">{event.description}</p>
                    )}
                    
                    <div className="event-management-page__event-details">
                      {event.start_date && (
                        <div className="event-management-page__event-date">
                          <strong>{t('management.events.startDate')}:</strong> {new Date(event.start_date).toLocaleDateString()}
                        </div>
                      )}
                      {event.end_date && (
                        <div className="event-management-page__event-date">
                          <strong>{t('management.events.endDate')}:</strong> {new Date(event.end_date).toLocaleDateString()}
                        </div>
                      )}
                      {event.address && (
                        <div className="event-management-page__event-location">
                          <strong>{t('management.events.location')}:</strong> {event.address}
                          {event.city && `, ${event.city}`}
                          {event.state && `, ${event.state}`}
                          {event.country && `, ${event.country}`}
                        </div>
                      )}
                      <div className="event-management-page__event-local">
                        <strong>{t('locals.title')}:</strong>{' '}
                        {event.local_name || <em>{t('locals.noLocal')}</em>}
                      </div>
                      {isSuperAdmin(user) && event.organizer_id && (() => {
                        const organizer = event.organizer || getOrganizerById(event.organizer_id);
                        return organizer ? (
                          <div className="event-management-page__event-organizer">
                            <strong>{t('management.events.organizer')}:</strong>{' '}
                            {getOrganizerDisplayName(organizer)}
                            {organizer.email && ` (${organizer.email})`}
                          </div>
                        ) : null;
                      })()}
                    </div>
                    
                    <div className="event-management-page__event-actions">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleEdit(event)}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleToggleActive(event.id)}
                      >
                        {event.active ? t('management.events.deactivate') : t('management.events.activate')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStartCloneEvent(event)}
                      >
                        {t('management.events.clone')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(event.id)}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>

                    {cloningEventId === event.id && (
                      <div className="event-management-page__clone-picker">
                        <FormField
                          type="text"
                          name="clone_name"
                          label={t('management.events.cloneNewName')}
                          value={cloneFormData.name}
                          onChange={(e) => setCloneFormData(prev => ({ ...prev, name: e.target.value }))}
                          disabled={isCloning}
                          required
                        />
                        <FormField
                          type="datetime-local"
                          name="clone_start_date"
                          label={t('management.events.startDate')}
                          value={cloneFormData.start_date}
                          onChange={(e) => setCloneFormData(prev => ({ ...prev, start_date: e.target.value }))}
                          disabled={isCloning}
                          required
                        />
                        <FormField
                          type="datetime-local"
                          name="clone_end_date"
                          label={t('management.events.endDate')}
                          value={cloneFormData.end_date || ''}
                          onChange={(e) => setCloneFormData(prev => ({ ...prev, end_date: e.target.value }))}
                          disabled={isCloning}
                        />
                        <div className="event-management-page__clone-actions">
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={!cloneFormData.name || !cloneFormData.start_date || isCloning}
                            onClick={() => handleConfirmCloneEvent(event.id)}
                          >
                            {isCloning ? t('common.loading') : t('management.events.clone')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelCloneEvent}
                            disabled={isCloning}
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                        <p className="event-management-page__clone-hint">
                          {t('management.events.cloneTeamHint')}
                        </p>
                      </div>
                    )}

                    {/* Venue Management Section */}
                    <div className="event-management-page__venue-section">
                      <div className="event-management-page__venue-header">
                        <h4>{t('management.events.venues.title')}</h4>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openVenueForm(event.id)}
                        >
                          {t('management.events.venues.addVenue')}
                        </Button>
                      </div>
                      
                      {(() => {
                        return venues[event.id] && venues[event.id].length > 0;
                      })() ? (
                        <div className="event-management-page__venues-list">
                          {venues[event.id].map((venue) => (
                            <div key={venue.id} className="event-management-page__venue-item">
                              <div className="event-management-page__venue-info">
                                <h5>{venue.name}</h5>
                                {venue.description && (
                                  <p>{venue.description}</p>
                                )}
                                <div className="event-management-page__venue-details">
                                  {venue.venue_type_name && <span>Type: {venue.venue_type_name}</span>}
                                  {/* An inactive event closes all its bars, whatever their own status */}
                                  {!event.active ? (
                                    <span
                                      className="status-badge inactive"
                                      title={venue.is_active ? t('management.events.venues.eventInactiveHint') : undefined}
                                    >
                                      {t('management.events.venues.eventInactive')}
                                    </span>
                                  ) : (
                                    <span className={`status-badge ${venue.is_active ? 'active' : 'inactive'}`}>
                                      {venue.is_active ? t('management.events.venues.active') : t('management.events.venues.inactive')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="event-management-page__venue-actions">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleVenueEdit(venue)}
                                >
                                  {t('common.edit')}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStartDuplicateVenue(venue.id)}
                                >
                                  {t('management.events.venues.duplicate')}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleVenueDelete(event.id, venue.id)}
                                >
                                  {t('common.delete')}
                                </Button>
                              </div>

                              {duplicatingVenueId === venue.id && (
                                <div className="event-management-page__venue-duplicate-picker">
                                  <SearchableSelect<number>
                                    name="duplicate_target_event"
                                    label={t('management.events.venues.duplicateTo')}
                                    placeholder={t('management.events.venues.selectTargetEvent')}
                                    clearable
                                    options={events.map((ev) => ({ value: ev.id, label: ev.name }))}
                                    value={duplicateTargetEventId || null}
                                    onChange={(value) => setDuplicateTargetEventId(value)}
                                    disabled={isDuplicating}
                                  />
                                  <div className="event-management-page__venue-duplicate-actions">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      disabled={!duplicateTargetEventId || isDuplicating}
                                      onClick={() => handleConfirmDuplicateVenue(venue.id)}
                                    >
                                      {isDuplicating ? t('common.loading') : t('management.events.venues.duplicate')}
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleCancelDuplicateVenue}
                                      disabled={isDuplicating}
                                    >
                                      {t('common.cancel')}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="event-management-page__no-venues">
                          {t('management.events.venues.noVenues')}
                        </p>
                      )}

                      <div className="event-management-page__venue-deleted-toggle">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleDeletedVenues(event.id)}
                        >
                          {showDeletedVenues[event.id]
                            ? t('management.events.venues.hideDeleted')
                            : t('management.events.venues.showDeleted')}
                        </Button>
                      </div>

                      {showDeletedVenues[event.id] && (
                        loadingDeletedVenues[event.id] ? (
                          <p>{t('common.loading')}</p>
                        ) : deletedVenues[event.id] && deletedVenues[event.id].length > 0 ? (
                          <div className="event-management-page__venues-list event-management-page__venues-list--deleted">
                            {deletedVenues[event.id].map((venue) => (
                              <div key={venue.id} className="event-management-page__venue-item event-management-page__venue-item--deleted">
                                <div className="event-management-page__venue-info">
                                  <h5>{venue.name}</h5>
                                  <div className="event-management-page__venue-details">
                                    <span className="status-badge deleted">
                                      {t('management.events.deleted')}
                                    </span>
                                  </div>
                                </div>
                                <div className="event-management-page__venue-actions">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleRestoreVenue(event.id, venue.id)}
                                  >
                                    {t('management.events.venues.restore')}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="event-management-page__no-venues">
                            {t('management.events.venues.noDeletedVenues')}
                          </p>
                        )
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )
          ) : (
            loadingDeleted ? (
              <Card variant="elevated" padding="lg">
                <p>{t('common.loading')}</p>
              </Card>
            ) : deletedEvents.length === 0 ? (
              <Card variant="elevated" padding="lg">
                <p>{t('management.events.noDeletedEvents')}</p>
              </Card>
            ) : (
              <div className="event-management-page__events-grid">
                {deletedEvents.map((event) => (
                  <Card key={event.id} variant="elevated" padding="lg" className="event-management-page__event-card event-management-page__event-card--deleted">
                    <div className="event-management-page__event-header">
                      <div className="event-management-page__event-title-section">
                        {event.logo && (
                          <img 
                            src={event.logo} 
                            alt={`${event.name} logo`}
                            className="event-management-page__event-logo"
                          />
                        )}
                        <h3>{event.name}</h3>
                      </div>
                      <div className="event-management-page__event-status">
                        <span className="event-management-page__status-badge deleted">
                          {t('management.events.deleted')}
                        </span>
                      </div>
                    </div>
                    
                    {event.description && (
                      <p className="event-management-page__event-description">{event.description}</p>
                    )}
                    
                    <div className="event-management-page__event-details">
                      {event.start_date && (
                        <div className="event-management-page__event-date">
                          <strong>{t('management.events.startDate')}:</strong> {new Date(event.start_date).toLocaleDateString()}
                        </div>
                      )}
                      {event.end_date && (
                        <div className="event-management-page__event-date">
                          <strong>{t('management.events.endDate')}:</strong> {new Date(event.end_date).toLocaleDateString()}
                        </div>
                      )}
                      {event.address && (
                        <div className="event-management-page__event-location">
                          <strong>{t('management.events.location')}:</strong> {event.address}
                          {event.city && `, ${event.city}`}
                          {event.state && `, ${event.state}`}
                          {event.country && `, ${event.country}`}
                        </div>
                      )}
                    </div>
                    
                    <div className="event-management-page__event-actions">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleRestoreEvent(event.id)}
                      >
                        {t('management.events.restore')}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default EventManagement; 